import React from 'react';
import './StageBar.scss';

type StageBarTier = 'capability' | 'substage';

interface StageBarProps {
  /**
   * The share of the track that renders as lost, 0–100, on the same base as `filteredPercent`. At the
   * capability tier pass the backend's `lossPercent`, never a ratio of `retained`/`lost` — those are mixed,
   * double-counted sums (see `FunnelStage`). A sub-stage with filtered records passes `lost ÷ itemsIn`
   * instead, so the three segments share one base.
   */
  lostPercent: number;
  /** Share of the track drawn as filtered, between retained and lost. Not hatched: filtered is not loss. */
  filteredPercent?: number;
  retainedColor: string;
  /** Which tier's track to draw. `capability` is the taller of the two; both fill their column. */
  tier?: StageBarTier;
  /** Inline label on the retained segment. Shown only at the capability tier, matching the mock. */
  retainedLabel?: React.ReactNode;
  lostLabel?: React.ReactNode;
}

/**
 * One funnel bar: a full-length track split into a retained fill and a hatched lost remainder.
 *
 * Every bar of a tier is the same length, which is what makes the funnel legible as a funnel: the lost
 * portions can be compared by eye down the column, and a stage that loses more shows a visibly longer
 * hatch. That equality comes from the track filling its grid column, which is the same width for every row
 * of a tier — no bar names a width of its own. Giving them explicit pixel widths is what previously
 * stranded them all at the left edge of a full-width panel.
 *
 * The lost segment is hatched, not just a second colour, so the split survives greyscale printing and
 * colour-blindness: the texture reads as "lost" independently of hue.
 */
export const StageBar: React.FC<StageBarProps> = ({
  lostPercent,
  filteredPercent = 0,
  retainedColor,
  tier = 'capability',
  retainedLabel,
  lostLabel,
}) => {
  // Clamp before use: a payload that reports 103% loss (or -2%) is wrong, but it should distort neither
  // the bar's geometry nor the layout around it. The printed figure elsewhere carries the raw value.
  const lost = Math.max(0, Math.min(100, lostPercent));
  const filtered = Math.max(0, Math.min(100 - lost, filteredPercent));
  const retained = 100 - lost - filtered;
  const showLabels = tier === 'capability';

  return (
    <div className={`ovp-stagebar ovp-stagebar--${tier}`}>
      {retained > 0 && (
        <div
          className="ovp-stagebar__segment ovp-stagebar__segment--keep"
          style={{ width: `${retained}%`, background: retainedColor }}
        >
          {showLabels && retainedLabel && (
            <span className="ovp-stagebar__label">{retainedLabel}</span>
          )}
        </div>
      )}
      {filtered > 0 && (
        <div
          className="ovp-stagebar__segment ovp-stagebar__segment--filtered"
          style={{ width: `${filtered}%` }}
        />
      )}
      {lost > 0 && (
        <div className="ovp-stagebar__segment ovp-stagebar__segment--lost" style={{ width: `${lost}%` }}>
          {showLabels && lostLabel && <span className="ovp-stagebar__label">{lostLabel}</span>}
        </div>
      )}
    </div>
  );
};
